import type { BoxShadow, BoxShadowLayer, TextShadow, Size } from './types';
import { stringifyColor, toColorOrThemeColor } from './color';
import { stringifySize, toSize } from './size';
import { separateLayers } from './layerUtils';
import { DefaultSizes } from '../../defaults';

export const getShadowStringParts = (shadow: string): Array<string> => {
  const PARTS_REGEX = /\s(?![^(]*\))/;
  return shadow.trim().split(PARTS_REGEX);
};

export const toTextShadowLayer = (shadow: string): TextShadow | undefined => {
  const parts = getShadowStringParts(shadow);
  const colorStr = parts.slice(-1)[0];
  const sizes = parts
    .filter(n => n !== colorStr)
    .map(toSize)
    .filter(Boolean) as Array<Size>;
  const [offsetX, offsetY, blurRadius = DefaultSizes.ZeroPixels] = sizes;

  const color = toColorOrThemeColor(colorStr);

  if (!offsetX || !offsetY || !color) {
    return;
  }

  return {
    propertyType: 'TextShadow',
    blurRadius,
    offsetX,
    offsetY,
    color,
  };
};

export const toBoxShadowLayer = (
  shadow: string,
): BoxShadowLayer | undefined => {
  const parts = getShadowStringParts(shadow);
  const inset = parts.includes('inset');
  const colorStr = parts.slice(-1)[0];

  const sizes = parts
    .filter(n => n !== 'inset')
    .filter(n => n !== colorStr)
    .map(toSize)
    .filter(Boolean) as Array<Size | undefined>;
  const [offsetX, offsetY, blurRadius, spreadRadius] = sizes;
  const color = toColorOrThemeColor(colorStr);

  if (!offsetX || !offsetY || !color) {
    return;
  }
  return {
    propertyType: 'BoxShadowLayer',
    offsetX,
    offsetY,
    inset,
    blurRadius: blurRadius ?? DefaultSizes.ZeroPixels,
    spreadRadius: spreadRadius ?? DefaultSizes.ZeroPixels,
    color,
  };
};

export const toBoxShadowLayers = (
  shadowString: string,
): BoxShadow | undefined => {
  if (!shadowString) {
    return undefined;
  }
  if (shadowString === 'none') {
    return {
      propertyType: 'BoxShadow',
      layers: [],
    };
  }

  const stringLayers = separateLayers(shadowString);
  const layers = stringLayers
    .map(toBoxShadowLayer)
    .filter(Boolean) as Array<BoxShadowLayer>;

  if (!layers.length) {
    return;
  }

  return {
    propertyType: 'BoxShadow',
    layers,
  };
};

export const stringifyTextShadowLayer = (shadow: TextShadow) => {
  const { offsetX, offsetY, blurRadius, color } = shadow;

  return `${stringifySize(offsetX)} ${stringifySize(offsetY)} ${stringifySize(
    blurRadius,
  )} ${stringifyColor(color)}`;
};

export const stringifyBoxShadowLayer = (shadow: BoxShadowLayer) => {
  const { inset, offsetX, offsetY, spreadRadius, blurRadius, color } = shadow;

  return `${inset ? 'inset ' : ''}${stringifySize(offsetX)} ${stringifySize(
    offsetY,
  )} ${stringifySize(blurRadius)} ${stringifySize(
    spreadRadius,
  )} ${stringifyColor(color)}`;
};

export const stringifyBoxShadowLayers = (shadow: BoxShadow): string => {
  if (!shadow.layers.length) {
    return 'none';
  }
  const shadowLayers = shadow.layers.map(stringifyBoxShadowLayer);
  return shadowLayers.join(', ').trim();
};

export const calculateAngleAndDistance = (
  offsetX: number = 0,
  offsetY: number = 0,
) => {
  const degreesPerRadians = 180 / Math.PI;

  const numericDistance = Math.round(
    Math.sqrt(offsetX * offsetX + offsetY * offsetY),
  );

  const radiansAngle = Math.atan2(offsetX, -offsetY);
  const numericAngle = parseFloat(
    ((180 + radiansAngle * degreesPerRadians) % 360).toFixed(0),
  );

  return {
    angle: numericAngle,
    distance: numericDistance,
  };
};
